// Local JWT inspection — payload decode + expiry check, no signature
// verification. Client-side heuristic only (the server remains the
// authority); used to tell a dead session apart from an application
// 401/403. See docs/FE_HANDOFF_session_expired_403.md (backend repo).

/** Decode a JWT's payload segment. Returns null for anything that doesn't
 *  decode to a JSON object. */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const segment = token.split('.')[1]
  if (!segment) {
    return null
  }
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0))
    const payload: unknown = JSON.parse(new TextDecoder().decode(bytes))
    return typeof payload === 'object' && payload !== null && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

/** True when the token can't be decoded, has no finite numeric `exp`, or
 *  `exp` is in the past. Fails closed: an unreadable token is a dead token.
 *  No clock-skew leeway — by the time this runs the server has already
 *  denied the request, so `exp` is only the tiebreaker. */
export function isJwtExpired(token: string): boolean {
  const payload = decodeJwtPayload(token)
  if (!payload) {
    return true
  }
  const exp = payload['exp']
  if (typeof exp !== 'number' || !Number.isFinite(exp)) {
    return true
  }
  return exp * 1000 <= Date.now()
}
